import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Public routes that should NOT trigger a redirect on 401
const PUBLIC_PATHS = ["/", "/why", "/privacy", "/terms", "/dpia", "/compatibility", "/status"];
const isPublicPath = () =>
  PUBLIC_PATHS.includes(window.location.pathname) ||
  window.location.pathname.startsWith("/handout/") ||
  window.location.pathname.startsWith("/certificate/") ||
  window.location.pathname.startsWith("/embed/") ||
  window.location.pathname.startsWith("/trust/") ||
  window.location.pathname.startsWith("/data-room/") ||
  window.location.pathname.startsWith("/integrations/");

// A mounted <UpgradeModal> registers here so the interceptor can show a
// branded, one-click upgrade prompt on any 402 (limit / Pro-gated) error.
let _upgradeHandler = null;
export const registerUpgradeHandler = (fn) => { _upgradeHandler = fn; };

// Shared error surfacer for page action handlers. Silently skips 402s
// (the upgrade modal already handled them) and shows a toast otherwise —
// replaces jarring native alert() popups.
export const notifyError = (err, fallback = "Something went wrong") => {
  if (err?.response?.status === 402 || err?.gateHandled) return;
  toast.error(err?.response?.data?.detail || err?.message || fallback);
};

// On 401 for authed endpoints, kick user back to landing. Avoids stale-session confusion.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && !isPublicPath()) {
      // Silent redirect — the landing page has the login CTA
      window.location.assign("/");
    }
    if (err?.response?.status === 402) {
      // Feature/limit gated — flag as handled and show the upgrade modal.
      err.gateHandled = true;
      const msg = err?.response?.data?.detail || "That's a Pro feature. Upgrade to unlock it.";
      if (_upgradeHandler) _upgradeHandler(msg);
      else toast.error(msg, {
        action: { label: "Upgrade", onClick: () => window.location.assign("/billing") },
        duration: 6000,
      });
    }
    return Promise.reject(err);
  }
);

export const streamSSE = async (url, body, onDelta, onDone) => {
  const resp = await fetch(`${API}${url}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(`SSE error ${resp.status}`);
  const reader = resp.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop() || "";
    for (const chunk of parts) {
      const lines = chunk.split("\n");
      let ev = "message"; let data = "";
      for (const l of lines) {
        if (l.startsWith("event: ")) ev = l.slice(7).trim();
        else if (l.startsWith("data: ")) data += l.slice(6);
      }
      if (ev === "done") { onDone && onDone(data); return; }
      if (data) onDelta && onDelta(data);
    }
  }
  onDone && onDone(null);
};
